import React, { FC, useState } from "react";
import { StyleSheet } from "react-native";
import { Dropdown } from "react-native-element-dropdown";

import { useAppTheme } from "@/hooks/useAppTheme";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { useTranslate } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";

import { VnBox } from "../VnBox";
import { VnText } from "../VnText/VnText";

type OnChangeObjectType = {
  _index: number;
  label: string;
  value: string;
};

export type SelectInputOptionType = {
  label: string;
  value: string;
};

export type BaseSelectInputPropsType = {
  onChange?: (value: string) => void;
  placeholderI18nKey?: I18nKeyType;
  placeholderI18nOptions?: object;
  placeholder?: string;
  isMultiline?: boolean;
  options: SelectInputOptionType[];
  onBlur?: () => void;
  onFocus?: () => void;
  defaultValue?: string;
  isDisabled?: boolean;
  value?: string;
  hasSearch?: boolean;
  hasError?: boolean;
};

export const BaseSelectInput: FC<BaseSelectInputPropsType> = ({
  value,
  placeholderI18nKey,
  placeholderI18nOptions,
  placeholder,
  onBlur,
  onFocus,
  onChange,
  options,
  isDisabled,
  hasSearch = false,
  hasError = false,
}) => {
  const { resolvedColors } = useAppTheme();

  const [isFocused, setIsFocused] = useState(false);
  const { isNative, isWeb } = usePlatformOS();

  const { isDarkMode } = useAppTheme();

  const placeholderText =
    useTranslate(placeholderI18nKey, placeholderI18nOptions) ?? placeholder;

  const styles = StyleSheet.create({
    dropdown: {
      height: 40,
      borderColor: resolvedColors.border,
      borderWidth: 1,
      borderRadius: 8,
      paddingHorizontal: 12,
      backgroundColor: isDarkMode
        ? `${resolvedColors.background}80` // 50% opacity in dark mode
        : resolvedColors.background,
    },
    withError: {
      borderColor: resolvedColors.destructive,
    },
    placeholderStyle: {
      fontSize: 16,
      color: resolvedColors.mutedForeground,
    },
    selectedTextStyle: {
      fontSize: 16,
      color: resolvedColors.foreground,
    },
    inputSearchStyle: {
      height: 40,
      fontSize: 16,
    },
    itemContainer: {
      backgroundColor: resolvedColors.background,
    },
    itemTextStyle: {
      fontSize: 16,
      color: resolvedColors.foreground,
    },
  });

  const renderItem = (item: OnChangeObjectType) => (
    <VnBox className="flex-1 px-4 py-3">
      <VnText variant="lg">{item.label}</VnText>
    </VnBox>
  );

  return (
    <Dropdown
      style={[
        styles.dropdown,
        isFocused && { borderColor: resolvedColors.primary },
        isNative && hasError && styles.withError,
      ]}
      containerStyle={isWeb && { pointerEvents: "auto" as const }}
      placeholderStyle={styles.placeholderStyle}
      selectedTextStyle={styles.selectedTextStyle}
      itemContainerStyle={styles.itemContainer}
      itemTextStyle={styles.itemTextStyle}
      renderItem={renderItem}
      data={options}
      activeColor={resolvedColors.accent}
      search={hasSearch}
      maxHeight={300}
      labelField="label"
      disable={isDisabled}
      onFocus={() => {
        setIsFocused(true);
        onFocus?.();
      }}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      valueField="value"
      placeholder={placeholderText}
      value={value}
      onChange={(item: OnChangeObjectType) => onChange?.(item.value)}
    />
  );
};
